import MiniSelect from "@/components/storyboard/MiniSelect";
import { SHOT_TYPES, CAMERA_MOVEMENTS, LENS_OPTIONS, LIGHTING_STYLES } from "@/constants/shotSettings";
import { RENDER_KINDS, resolveWorld } from "@/constants/breakdownSettings";
import { Monitor, AlertTriangle, Film } from "lucide-react";

/**
 * One editable parsed shot row in the import-preview table.
 * Enum cells are MiniSelect dropdowns bound to the canonical shotSettings arrays;
 * a low-confidence parse shows an amber flag, screen-capture rows are badged and
 * de-emphasise the cinematic cells (lens/lighting don't apply to a UI capture).
 */
export default function BreakdownRow({ row, onChange }) {
  const isScreen = row.render_kind === "screen_capture";
  const world = resolveWorld(row.world_tag);
  const low = row.confidence === "low";

  const set = (field) => (value) => onChange({ ...row, [field]: value });

  return (
    <div
      className={`relative flex items-stretch gap-2 px-3 py-2 rounded-lg border transition-colors ${
        low ? "border-amber-500/40 bg-amber-500/[0.04]" : "border-[var(--border-subtle)] bg-[var(--bg-muted)]"
      }`}
    >
      {/* world color-bar */}
      <span className={`absolute left-0 top-1 bottom-1 w-1 rounded-full ${world.bar}`} aria-hidden />

      {/* number + tc */}
      <div className="flex flex-col items-center justify-center pl-1.5 w-12 flex-shrink-0">
        <span className="text-xs font-semibold text-[var(--text-primary)]">#{row.shot_number}</span>
        <span className="text-[11px] text-[var(--text-muted)]">{row.timecode}</span>
      </div>

      {/* enum cells */}
      <div className="grid grid-cols-2 gap-1.5 w-44 flex-shrink-0 self-center">
        <MiniSelect options={SHOT_TYPES} value={row.shot_type} onChange={set("shot_type")} label="shot…" />
        <MiniSelect options={CAMERA_MOVEMENTS} value={row.camera_movement} onChange={set("camera_movement")} label="move…" />
        <div className={isScreen ? "opacity-40 pointer-events-none" : ""}>
          <MiniSelect options={LENS_OPTIONS} value={row.lens} onChange={set("lens")} label="lens…" />
        </div>
        <div className={isScreen ? "opacity-40 pointer-events-none" : ""}>
          <MiniSelect options={LIGHTING_STYLES} value={row.lighting} onChange={set("lighting")} label="light…" />
        </div>
      </div>

      {/* what's in frame */}
      <div className="flex-1 min-w-0 self-center">
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug line-clamp-2">
          {row.whats_in_frame}
        </p>
        {row.audio_cut ? (
          <p className="text-[11px] text-[var(--text-muted)] truncate mt-0.5">♪ {row.audio_cut}</p>
        ) : null}
      </div>

      {/* right rail: world + render kind + flags */}
      <div className="flex flex-col items-end justify-center gap-1 w-28 flex-shrink-0">
        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${world.chip}`}>{world.label}</span>
        <button
          onClick={() => onChange({ ...row, render_kind: isScreen ? "photoreal" : "screen_capture" })}
          className={`text-[11px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${
            isScreen
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
              : "bg-[var(--bg-elevated)] text-[var(--text-muted)] border-[var(--border-subtle)]"
          }`}
          title="Toggle screen-capture (UI) vs photoreal rendering"
        >
          {isScreen ? <Monitor size={10} /> : <Film size={10} />}
          {isScreen ? "Screen/UI" : "Photoreal"}
        </button>
        {row.expand_target_count ? (
          <span className="text-[11px] text-[var(--color-gold-400)]">~{row.expand_target_count} expandable</span>
        ) : null}
        {low ? (
          <span className="text-[11px] text-amber-400 flex items-center gap-0.5">
            <AlertTriangle size={9} /> review
          </span>
        ) : null}
      </div>
    </div>
  );
}
